import {
  For,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";
import { Portal } from "solid-js/web";

import { useClient } from "@revolt/client";
import { useNavigate } from "@revolt/routing";

import { WelcomeMoment, playWelcome } from "./WelcomeMoment";

/**
 * Majalis welcome tour: a short, skippable pop-up shown once to new members.
 * Progress is saved to the account (synced settings) so it never repeats.
 * Survey answers are saved to the account too; a bot posts them to a private channel.
 */

const ONBOARD_KEY = "majalis_onboarding";
const SURVEY_KEY = "majalis_survey";
const NEW_ACCOUNT_DAYS = 3;

type StepId =
  | "welcome"
  | "name"
  | "install"
  | "q1"
  | "q2"
  | "q3"
  | "rules";

const STEPS: StepId[] = [
  "welcome",
  "name",
  "install",
  "q1",
  "q2",
  "q3",
  "rules",
];

const WHY = [
  "Learn from educators",
  "Join live seminars",
  "Connect with others",
  "Support the community",
];
const HEARD = ["A friend", "Social media", "An educator", "Somewhere else"];
const TOPICS = [
  "Islamic Studies",
  "Islamic Sciences",
  "Qur'an Recitation",
  "Health",
  "Fitness",
  "Marriage",
  "Business & Entrepreneur",
  "Finance & Wealth",
  "Technology & AI",
  "Personal Development",
  "Career",
];

// keep the browser's install prompt so the install step can open it
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let deferredPrompt: any = null;
const [canPrompt, setCanPrompt] = createSignal(false);
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    setCanPrompt(true);
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    setCanPrompt(false);
  });
}

// lets other parts of the app (e.g. Settings) reopen the tour
const [tourRequest, setTourRequest] = createSignal(0);
export const requestTour = () => setTourRequest((n) => n + 1);

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** Creation time of an account, read from its ULID-style id (ms since epoch) */
function idTime(id: string): number {
  let t = 0;
  for (const ch of id.slice(0, 10).toUpperCase()) {
    const v = CROCKFORD.indexOf(ch);
    if (v < 0) return 0;
    t = t * 32 + v;
  }
  return t;
}

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).standalone === true
    );
  } catch {
    return false;
  }
}

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const CSS =
  ".mj-tour button{font-family:inherit}" +
  ".mj-tour button:focus-visible{outline:2px solid #FFC62E;outline-offset:2px}";

const rowStyle = (sel: boolean) =>
  "display:flex;align-items:center;gap:12px;width:100%;box-sizing:border-box;padding:14px 16px;border-radius:12px;font-size:15px;color:#fff;text-align:left;cursor:pointer;" +
  (sel
    ? "background:#241d0c;border:1.5px solid #E2B04A;"
    : "background:#1c1c22;border:1.5px solid #2f2f39;");

const markStyle = (sel: boolean) =>
  "flex-shrink:0;width:18px;height:18px;border-radius:50%;box-sizing:border-box;" +
  (sel
    ? "background:#FFC62E;border:5px solid #241d0c;box-shadow:0 0 0 1.5px #FFC62E;"
    : "background:transparent;border:1.5px solid #6a6a78;");

const chipStyle = (sel: boolean) =>
  "padding:10px 15px;white-space:nowrap;border-radius:999px;font-size:14px;font-weight:600;cursor:pointer;" +
  (sel
    ? "background:#E2B04A;border:1.5px solid #E2B04A;color:#1a1200;"
    : "background:#1c1c22;border:1.5px solid #2f2f39;color:#e6e6ee;");

const title =
  "font-size:26px;font-weight:700;line-height:1.2;letter-spacing:-0.01em;margin:0;";
const body = "font-size:15px;line-height:1.65;color:#c9c9d3;margin:0;";
const muted = "font-size:14px;line-height:1.6;color:#9a9aa8;margin:0;";
const eyebrow =
  "font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#E2B04A;";
const iconBox =
  "width:56px;height:56px;border-radius:16px;background:#241d0c;border:1px solid #5a4716;display:flex;align-items:center;justify-content:center;";
const numRow =
  "display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:12px;background:#1c1c22;border:1px solid #2f2f39;";
const numDot =
  "width:26px;height:26px;border-radius:50%;background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;";
const rule =
  "border-left:2px solid #E2B04A;padding:2px 0 2px 14px;font-size:14px;line-height:1.6;color:#e6e6ee;";

const tight = rule.replace(
  "padding:2px 0 2px 14px;font-size:14px;line-height:1.6;",
  "padding:2px 0 2px 14px;font-size:15px;line-height:1.5;",
);

/* ---------- App tour: short spotlight walk-through of the real interface ---------- */
type SpotStep = { target?: string; title: string; text: string };

const SPOT: SpotStep[] = [
  {
    target: "dash",
    title: "Your Dashboard",
    text: "Seminar times, prayer times and your messages are all here. Open it any time with the Dashboard tab.",
  },
  {
    target: "rooms",
    title: "The Majalis rooms",
    text: "This opens every room. They are grouped by topic, so tap a heading to open or close a group.",
  },
  {
    title: "Say salaam, find seminars",
    text: "Introduce yourself in the introductions room. Seminar times are posted in your Schedule room, and notifications there make sure you never miss one.",
  },
  {
    target: "friends",
    title: "Friends and private messages",
    text: "To message a member privately, send a friend request from Friends. Questions for educators go in the Q&A rooms.",
  },
  {
    target: "help",
    title: "Need a hand?",
    text: "Click the support card on the Dashboard and a private ticket opens with our team.",
  },
];

// lets other parts of the app (e.g. Settings) replay just the app tour
const [spotRequest, setSpotRequest] = createSignal(0);
export const requestAppTour = () => setSpotRequest((n) => n + 1);

function SpotlightView(props: {
  step: SpotStep;
  i: number;
  total: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const [rect, setRect] = createSignal<DOMRect | null>(null);
  const [vw, setVw] = createSignal(window.innerWidth);
  const [vh, setVh] = createSignal(window.innerHeight);
  let primary: HTMLButtonElement | undefined;
  let card: HTMLDivElement | undefined;
  const [ch, setCh] = createSignal(210);

  const measure = () => {
    setVw(window.innerWidth);
    setVh(window.innerHeight);
    if (card) setCh(card.offsetHeight || 210);
    const t = props.step.target;
    const el = t
      ? (document.querySelector('[data-tour="' + t + '"]') as HTMLElement | null)
      : null;
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight) {
        setRect(r);
        return;
      }
    }
    // target not on screen (e.g. on a phone): show the tip in the middle instead
    setRect(null);
  };

  createEffect(() => {
    props.step;
    measure();
    const a = window.setTimeout(measure, 200);
    const b = window.setTimeout(measure, 800);
    queueMicrotask(() => primary?.focus());
    onCleanup(() => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    });
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onSkip();
    } else if (e.key === "ArrowRight") props.onNext();
    else if (e.key === "ArrowLeft" && props.i > 0) props.onBack();
  };
  onMount(() => {
    window.addEventListener("resize", measure);
    window.addEventListener("keydown", onKey);
  });
  onCleanup(() => {
    window.removeEventListener("resize", measure);
    window.removeEventListener("keydown", onKey);
  });

  const cardW = () => Math.min(320, vw() - 24);
  const cardXY = (): [number, number] => {
    const r = rect();
    const w = cardW();
    if (!r) return [(vw() - w) / 2, Math.max(12, (vh() - ch()) / 2)];
    if (r.right + 16 + w <= vw() - 12 && r.width < vw() * 0.5)
      return [r.right + 16, Math.min(Math.max(12, r.top), vh() - ch() - 12)];
    return [
      Math.min(Math.max(12, r.left + r.width / 2 - w / 2), vw() - w - 12),
      r.bottom + 16 + ch() <= vh() - 12
        ? r.bottom + 16
        : Math.max(12, r.top - 16 - ch()),
    ];
  };
  const cardPos = () => {
    const c = cardXY();
    return "left:" + c[0] + "px;top:" + c[1] + "px;";
  };

  // the highlight fades out, then fades in at the next spot (no travelling)
  const holeBox = () => {
    const r = rect();
    return r
      ? [r.left - 6, r.top - 6, r.width + 12, r.height + 12]
      : [vw() / 2, vh() / 2, 0, 0];
  };
  const [box, setBox] = createSignal<number[]>(holeBox());
  const [holeVis, setHoleVis] = createSignal(true);
  let fading = false;
  createEffect(
    on(
      () => props.step,
      () => {
        fading = true;
        setHoleVis(false);
        window.setTimeout(() => {
          fading = false;
          setBox(holeBox());
          setHoleVis(true);
        }, 170);
      },
      { defer: true },
    ),
  );
  createEffect(() => {
    const bx = holeBox();
    if (!fading) setBox(bx);
  });
  const hole = () => {
    const bx = box();
    const has = bx[2] > 0;
    return (
      "left:" + bx[0] + "px;top:" + bx[1] + "px;width:" + bx[2] + "px;height:" + bx[3] +
      "px;border-radius:14px;box-sizing:border-box;border:2px solid " + (has ? "#E2B04A" : "transparent") +
      ";box-shadow:0 0 0 9999px rgba(0,0,0,0.4)" + (has ? ",0 0 24px rgba(255,198,46,0.35)" : "") +
      ";transition:opacity " + (holeVis() ? ".24s" : ".15s") + " ease;opacity:" + (holeVis() ? 1 : 0) + ";"
    );
  };

  // the card keeps its glide, with a touch of motion blur (skipped for reduced motion)
  const reduceMotion =
    !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let prevCard: [number, number] | null = null;
  createEffect(() => {
    const c = cardXY();
    if (!reduceMotion && prevCard && card) {
      const d = Math.hypot(c[0] - prevCard[0], c[1] - prevCard[1]);
      if (d > 40) {
        const bl = Math.min(1.5, d / 200);
        card.animate(
          [{ filter: "blur(0px)" }, { filter: "blur(" + bl + "px)", offset: 0.3 }, { filter: "blur(0px)" }],
          { duration: 550, easing: "ease-out" },
        );
      }
    }
    prevCard = c;
  });

  return (
    <div
      class="mj-tour"
      style="position:fixed;top:0;right:0;bottom:0;left:0;z-index:10001;"
    >
      <style>{CSS}</style>
      <div
        style="position:absolute;top:0;right:0;bottom:0;left:0;"
        onClick={(e) => e.stopPropagation()}
      />
      <div aria-hidden="true" style="position:absolute;top:0;right:0;bottom:0;left:0;background:rgba(0,0,0,0.5);pointer-events:none;" />
      <div aria-hidden="true" style={"position:fixed;pointer-events:none;" + hole()} />
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-label={props.step.title}
        style={
          "position:fixed;width:" + cardW() +
          "px;box-sizing:border-box;background:#15151a;border:1px solid #2a2a33;border-radius:16px;padding:18px 20px;color:#fff;box-shadow:0 20px 50px rgba(0,0,0,0.6);transition:left .5s cubic-bezier(.4,0,.1,1) .04s,top .5s cubic-bezier(.4,0,.1,1) .04s;" +
          cardPos()
        }
      >
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div style={eyebrow}>
            {props.i + 1} of {props.total}
          </div>
          <button
            onClick={props.onSkip}
            style="background:transparent;border:none;color:#9a9aa8;font-size:13px;cursor:pointer;padding:4px;"
          >
            Skip tour
          </button>
        </div>
        <Show when={props.step} keyed>
          {(st) => (
            <div style="animation:mjTipIn .45s ease both;">
              <style>
                {"@keyframes mjTipIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion:reduce){.mj-tour *{transition:none!important;animation:none!important}}"}
              </style>
              <h3 style="font-size:18px;font-weight:700;margin:10px 0 6px;line-height:1.25;">
                {st.title}
              </h3>
              <p style={body}>{st.text}</p>
            </div>
          )}
        </Show>
        <div style="display:flex;gap:10px;margin-top:16px;">
          <Show when={props.i > 0}>
            <button
              onClick={props.onBack}
              style="padding:11px 18px;border-radius:12px;border:1.5px solid #2f2f39;background:transparent;color:#e6e6ee;font-size:14px;font-weight:600;cursor:pointer;"
            >
              Back
            </button>
          </Show>
          <button
            ref={primary}
            onClick={props.onNext}
            style="flex-grow:1;padding:12px 18px;border-radius:12px;border:none;background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;font-size:15px;font-weight:700;cursor:pointer;"
          >
            {props.i + 1 >= props.total ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Onboarding() {
  const client = useClient();
  const navigate = useNavigate();

  const [open, setOpen] = createSignal(false);
  const [idx, setIdx] = createSignal(0);
  const [away, setAway] = createSignal(false);
  const [why, setWhy] = createSignal<string[]>([]);
  const [heard, setHeard] = createSignal<string | null>(null);
  const [topics, setTopics] = createSignal<string[]>([]);
  const [first, setFirst] = createSignal("");
  const [last, setLast] = createSignal("");
  const fullName = () => (first().trim() + " " + last().trim()).trim().slice(0, 32);
  // start from the name they already have, if any
  createEffect(
    on(open, (o) => {
      if (!o) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dn = String((client()?.user as any)?.displayName ?? "").trim();
      if (!dn) return;
      const parts = dn.split(/\s+/);
      setFirst(parts[0] ?? "");
      setLast(parts.slice(1).join(" "));
    }),
  );
  let autoShown = false;
  let primaryRef: HTMLButtonElement | undefined;

  const [spot, setSpot] = createSignal<number | null>(null);
  const startSpot = () => {
    try {
      navigate("/app");
    } catch {
      /* ignore */
    }
    setSpot(0);
  };
  const endSpot = () => {
    setSpot(null);
    playWelcome();
  };
  const cur = () => SPOT[spot() ?? 0];
  createEffect(
    on(
      spotRequest,
      (n) => {
        if (n > 0) startSpot();
      },
      { defer: true },
    ),
  );

  const steps = createMemo(() =>
    isStandalone() ? STEPS.filter((s) => s !== "install") : STEPS,
  );
  const step = () => steps()[Math.min(idx(), steps().length - 1)];
  const sections = createMemo(() => steps().map((_, i) => i));
  const segIndex = () => Math.min(idx(), steps().length - 1);
  const isLast = () => idx() >= steps().length - 1;

  const installMode = () =>
    isIos() ? "ios" : canPrompt() ? "prompt" : "manual";

  const userId = () => client()?.user?.id;

  onMount(async () => {
    const uid = userId();
    if (!uid) return;

    const forced = new URLSearchParams(window.location.search).get("tour") === "1";
    if (forced) {
      setOpen(true);
      return;
    }

    try {
      if (localStorage.getItem("majalis:onboarded:" + uid)) return;
    } catch {
      /* ignore */
    }

    // only brand-new accounts see it automatically
    const ageDays = (Date.now() - idTime(uid)) / 86400000;
    if (!(ageDays >= 0 && ageDays <= NEW_ACCOUNT_DAYS)) return;

    try {
      const res = (await client()!.api.post("/sync/settings/fetch", {
        keys: [ONBOARD_KEY],
      })) as unknown as Record<string, [number, string]>;
      if (res && res[ONBOARD_KEY]) {
        try {
          localStorage.setItem("majalis:onboarded:" + uid, "1");
        } catch {
          /* ignore */
        }
        return;
      }
    } catch (err) {
      console.warn("[majalis] tour check failed", err);
    }

    autoShown = true;
    setOpen(true);
  });

  // reopen on request (e.g. from Settings)
  createEffect(
    on(
      tourRequest,
      (n) => {
        if (n > 0) {
          setIdx(0);
          setAway(false);
          setOpen(true);
        }
      },
      { defer: true },
    ),
  );

  // move keyboard focus to the main button on each step
  createEffect(() => {
    if (open()) {
      step();
      queueMicrotask(() => primaryRef?.focus());
    }
  });

  const finish = (runTour = false) => {
    setAway(false);
    setOpen(false);
    if (runTour) setTimeout(startSpot, 150);
    else playWelcome();
    const uid = userId();
    if (!autoShown || !uid) return;
    autoShown = false;
    try {
      localStorage.setItem("majalis:onboarded:" + uid, "1");
    } catch {
      /* ignore */
    }
    const payload: Record<string, unknown> = {
      [ONBOARD_KEY]: "done",
      timestamp: +new Date(),
    };
    if (why().length || heard() || topics().length) {
      payload[SURVEY_KEY] = JSON.stringify({
        why: why(),
        topics: topics(),
        heard: heard(),
        at: new Date().toISOString(),
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (client()!.api as any)
      .post("/sync/settings/set", payload)
      .catch((err: unknown) => console.warn("[majalis] tour save failed", err));
  };

  const toggleWhy = (t: string) =>
    setWhy((w) => (w.includes(t) ? w.filter((x) => x !== t) : [...w, t]));

  const next = () => (isLast() ? finish(true) : setIdx((i) => i + 1));
  const back = () => setIdx((i) => Math.max(0, i - 1));

  const installNow = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {
        /* ignore */
      }
      deferredPrompt = null;
      setCanPrompt(false);
    }
    next();
  };

  const primaryLabel = () => {
    const s = step();
    if (s === "welcome") return "Get started";
    if (s === "install" && installMode() === "prompt") return "Install app";
    if (s === "rules") return "Show me around";
    return "Next";
  };
  const saveName = () => {
    const full = fullName();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cur = String((client()?.user as any)?.displayName ?? "");
    if (!full || full === cur) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (client()!.api as any)
      .patch("/users/@me", { display_name: full })
      .catch((err: unknown) => console.warn("[majalis] name save failed", err));
  };
  const primaryAction = () => {
    if (step() === "name") saveName();
    if (step() === "install" && installMode() === "prompt") installNow();
    else next();
  };

  const toggleTopic = (t: string) =>
    setTopics((list) =>
      list.includes(t) ? list.filter((x) => x !== t) : [...list, t],
    );

  return (
    <>
      <WelcomeMoment />
      <Show when={spot() !== null}>
        <Portal>
          <SpotlightView
            step={cur()}
            i={spot() ?? 0}
            total={SPOT.length}
            onNext={() =>
              (spot() ?? 0) >= SPOT.length - 1 ? endSpot() : setSpot((spot() ?? 0) + 1)
            }
            onBack={() => setSpot(Math.max(0, (spot() ?? 0) - 1))}
            onSkip={endSpot}
          />
        </Portal>
      </Show>
    <Show when={open()}>
      <Portal>
        <style>{CSS}</style>
        <Show when={!away()}>
        <div
          class="mj-tour"
          style="position:fixed;top:0;right:0;bottom:0;left:0;z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;background:rgba(0,0,0,0.5);backdrop-filter:blur(22px);-webkit-backdrop-filter:blur(22px);"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Welcome to Majalis"
            style="width:392px;max-width:100%;height:min(668px,calc(100dvh - 32px));box-sizing:border-box;display:flex;flex-direction:column;background:#15151a;border:1px solid #2a2a33;border-radius:20px;padding:22px 24px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:#fff;"
          >
            <div style="display:flex;align-items:center;justify-content:space-between;">
              <div style="font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#9a9aa8;">
                Step {segIndex() + 1} of {sections().length}
              </div>
              <button
                onClick={() => finish(false)}
                style="background:transparent;border:none;color:#9a9aa8;font-size:13px;cursor:pointer;padding:6px 4px;"
              >
                Skip for now
              </button>
            </div>

            <div style="display:flex;gap:6px;margin-top:12px;">
              <For each={sections()}>
                {(_, k) => (
                  <div
                    style={
                      "flex:1;height:4px;border-radius:2px;background:" +
                      (k() <= segIndex() ? "#E2B04A" : "#2a2a33")
                    }
                  />
                )}
              </For>
            </div>

            <div style="flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;padding-top:28px;">
              <Switch>
                <Match when={step() === "welcome"}>
                  <div style="display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;flex-grow:1;justify-content:center;">
                    <div
                      role="img"
                      aria-label="Majalis emblem"
                      style="width:120px;aspect-ratio:974/853;display:block;background:linear-gradient(180deg,#FFC420,#E69500);-webkit-mask:url(/assets/web/logo.svg) center/contain no-repeat;mask:url(/assets/web/logo.svg) center/contain no-repeat;filter:drop-shadow(0 0 14px rgba(255,160,0,0.3));"
                    />
                    <div style="font-size:13px;font-weight:600;letter-spacing:0.34em;padding-left:0.34em;text-transform:uppercase;color:#E2B04A;margin-top:6px;">
                      Majalis
                    </div>
                    <div style="font-size:38px;font-weight:700;line-height:1.15;letter-spacing:-0.015em;margin-top:-1px;">
                      Asalamu Alaykum.
                    </div>
                    <img src="/assets/web/salaam.svg" alt="السلام عليكم" lang="ar" style="width:170px;height:auto;display:block;margin-top:7px;filter:drop-shadow(0 0 4px rgba(255,198,46,.3)) drop-shadow(0 0 12px rgba(242,157,10,.18));" />
                    <div aria-hidden="true" style="width:40px;height:1px;background:#4a4a58;margin:-4px 0;"></div>
                    <p style={body + "max-width:300px;"}>
                      Let's get you settled in. It takes about a minute, and you
                      can skip any step.
                    </p>
                  </div>
                </Match>

                <Match when={step() === "name"}>
                  <div style="display:flex;flex-direction:column;gap:14px;">
                    <div style={iconBox}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <circle cx="12" cy="8" r="3.6" />
                        <path d="M4.5 20.5c.8-3.6 3.8-5.6 7.5-5.6s6.7 2 7.5 5.6" />
                      </svg>
                    </div>
                    <h2 style={title}>What should we call you?</h2>
                    <p style={body}>
                      Other members will see this name. A first name is perfectly
                      fine.
                    </p>
                    <input
                      type="text"
                      autocomplete="given-name"
                      maxLength={16}
                      placeholder="First name"
                      value={first()}
                      onInput={(e) => setFirst(e.currentTarget.value)}
                      onKeyDown={(e) => e.key === "Enter" && primaryAction()}
                      style="width:100%;box-sizing:border-box;padding:14px 16px;border-radius:12px;background:#1c1c22;border:1.5px solid #2f2f39;color:#fff;font-size:16px;font-family:inherit;outline:none;"
                    />
                    <input
                      type="text"
                      autocomplete="family-name"
                      maxLength={16}
                      placeholder="Last name (optional)"
                      value={last()}
                      onInput={(e) => setLast(e.currentTarget.value)}
                      onKeyDown={(e) => e.key === "Enter" && primaryAction()}
                      style="width:100%;box-sizing:border-box;padding:14px 16px;border-radius:12px;background:#1c1c22;border:1.5px solid #2f2f39;color:#fff;font-size:16px;font-family:inherit;outline:none;"
                    />
                    <p style={muted}>
                      <Show
                        when={fullName()}
                        fallback={"Leave both blank to keep your username."}
                      >
                        You'll appear as <strong style="color:#fff;">{fullName()}</strong>. You
                        can change it any time in Settings.
                      </Show>
                    </p>
                  </div>
                </Match>

                <Match when={step() === "install"}>
                  <div style="display:flex;flex-direction:column;gap:14px;">
                    <div style={iconBox}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
                        <path d="M11 18.5h2" />
                      </svg>
                    </div>
                    <h2 style={title}>Install Majalis on your phone</h2>
                    <p style={body}>
                      You get seminar reminders and a smoother experience. It
                      opens like any other app.
                    </p>

                    <Show when={installMode() === "prompt"}>
                      <div style="display:flex;flex-direction:column;align-items:flex-start;gap:6px;margin-top:6px;">
                        <p style={muted}>One tap, and you stay signed in.</p>
                        <button
                          onClick={next}
                          style="background:transparent;border:none;color:#9a9aa8;font-size:13px;text-decoration:underline;cursor:pointer;padding:6px 0;"
                        >
                          Maybe later
                        </button>
                      </div>
                    </Show>

                    <Show when={installMode() === "ios"}>
                      <div style="display:flex;flex-direction:column;gap:10px;margin-top:4px;">
                        <div style={numRow}>
                          <div style={numDot}>1</div>
                          <div style="font-size:14px;line-height:1.5;color:#e6e6ee;flex-grow:1;">
                            Tap the Share button in Safari
                          </div>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M12 15V3" />
                            <path d="M8 7l4-4 4 4" />
                            <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
                          </svg>
                        </div>
                        <div style={numRow}>
                          <div style={numDot}>2</div>
                          <div style="font-size:14px;line-height:1.5;color:#e6e6ee;flex-grow:1;">
                            Choose "Add to Home Screen"
                          </div>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <rect x="4" y="4" width="16" height="16" rx="3" />
                            <path d="M12 8v8" />
                            <path d="M8 12h8" />
                          </svg>
                        </div>
                        <div style={numRow}>
                          <div style={numDot}>3</div>
                          <div style="font-size:14px;line-height:1.5;color:#e6e6ee;flex-grow:1;">
                            Open Majalis from your home screen and sign in once
                            more
                          </div>
                        </div>
                      </div>
                    </Show>

                    <Show when={installMode() === "manual"}>
                      <p style={muted}>
                        Open your browser menu and choose "Install app" or "Add
                        to Home Screen".
                      </p>
                    </Show>
                  </div>
                </Match>

                <Match when={step() === "q1"}>
                  <div style="display:flex;flex-direction:column;gap:12px;">
                    <div style={eyebrow}>Question 1 of 3</div>
                    <h2 style={title}>What brings you to Majalis?</h2>
                    <p style={muted + "margin-bottom:6px;"}>
                      Choose as many as you like. This only helps us shape the
                      community.
                    </p>
                    <div style="display:flex;flex-wrap:wrap;gap:10px;">
                      <For each={WHY}>
                        {(label) => (
                          <button
                            aria-pressed={why().includes(label)}
                            onClick={() => toggleWhy(label)}
                            style={chipStyle(why().includes(label))}
                          >
                            {label}
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                </Match>

                <Match when={step() === "q2"}>
                  <div style="display:flex;flex-direction:column;gap:12px;">
                    <div style={eyebrow}>Question 2 of 3</div>
                    <h2 style={title}>Which topics interest you most?</h2>
                    <p style={muted + "margin-bottom:6px;"}>
                      Choose as many as you like. They match the rooms you'll
                      find in Majalis.
                    </p>
                    <div style="display:flex;flex-wrap:wrap;gap:10px;">
                      <For each={TOPICS}>
                        {(label) => (
                          <button
                            aria-pressed={topics().includes(label)}
                            onClick={() => toggleTopic(label)}
                            style={chipStyle(topics().includes(label))}
                          >
                            {label}
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                </Match>

                <Match when={step() === "q3"}>
                  <div style="display:flex;flex-direction:column;gap:12px;">
                    <div style={eyebrow}>Question 3 of 3</div>
                    <h2 style={title}>How did you hear about Majalis?</h2>
                    <p style={muted + "margin-bottom:6px;"}>
                      Last one. Thank you.
                    </p>
                    <For each={HEARD}>
                      {(label) => (
                        <button
                          aria-pressed={heard() === label}
                          onClick={() => setHeard(label)}
                          style={rowStyle(heard() === label)}
                        >
                          <span style={markStyle(heard() === label)} />
                          <span>{label}</span>
                        </button>
                      )}
                    </For>
                  </div>
                </Match>

                <Match when={step() === "rules"}>
                  <div style="display:flex;flex-direction:column;gap:14px;">
                    <h2 style={title}>Community guidelines</h2>
                    <div style="display:flex;flex-direction:column;gap:12px;">
                      <div style={tight}>
                        <strong>Keep it halal.</strong> No backbiting or mockery.
                      </div>
                      <div style={tight}>Be respectful and kind to everyone.</div>
                      <div style={tight}>
                        <strong>Debate with adab.</strong> Respectful discussion of
                        differences is welcome.
                      </div>
                      <div style={tight}>No spam or self-promotion.</div>
                      <div style={tight.replace("#E2B04A", "#FFC62E").replace("#e6e6ee", "#ffffff")}>
                        <strong>Content here is for members only.</strong>{" "}
                        Sharing seminars, recordings, or library material outside
                        Majalis may lead to removal without a refund.
                      </div>
                    </div>
                  </div>
                </Match>

              </Switch>
            </div>

            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding-top:16px;">
              <Show when={idx() > 0}>
                <button
                  onClick={back}
                  style="padding:14px 22px;border-radius:12px;border:1.5px solid #2f2f39;background:transparent;color:#e6e6ee;font-size:15px;font-weight:600;cursor:pointer;"
                >
                  Back
                </button>
              </Show>
              <button
                ref={primaryRef}
                onClick={primaryAction}
                style="flex-grow:1;padding:15px 22px;border-radius:12px;border:none;background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;font-size:16px;font-weight:700;cursor:pointer;box-shadow:0 0 18px rgba(255,184,28,0.25);"
              >
                {primaryLabel()}
              </button>
            </div>
          </div>
        </div>
        </Show>
        <Show when={away()}>
          <div
            class="mj-tour"
            style="position:fixed;left:0;right:0;bottom:92px;z-index:10000;display:flex;justify-content:center;pointer-events:none;"
          >
            <div style="pointer-events:auto;display:flex;align-items:center;gap:2px;background:#15151a;border:1px solid #5a4716;border-radius:999px;padding:5px 6px 5px 5px;box-shadow:0 10px 30px rgba(0,0,0,0.6);">
              <button
                onClick={() => setAway(false)}
                style="padding:12px 20px;border-radius:999px;border:none;background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;font-size:15px;font-weight:700;cursor:pointer;"
              >
                Back to tour
              </button>
              <button
                aria-label="Close tour"
                onClick={() => finish(false)}
                style="background:transparent;border:none;color:#9a9aa8;font-size:22px;line-height:1;padding:8px 12px;cursor:pointer;"
              >
                &times;
              </button>
            </div>
          </div>
        </Show>
      </Portal>
    </Show>
    </>
  );
}
